// A5 — THE TIMER STOP HANDLERS. Brief 65 (brief 50 §4), the fourth of the sixteen.
//
// Two groups, both about taking a running timer OFF the list, and the census
// reports three pairs across five blocks:
//
//   src/App.tsx:3504-3522@8fdbbd6c x src/App.tsx:3524-3541@8fdbbd6c  87.3%  the stop handlers
//   src/App.tsx:3503-3523@8fdbbd6c x src/App.tsx:3524-3541@8fdbbd6c  84.4%  (the same, one line wider)
//   src/App.tsx:5809-5823@8fdbbd6c x src/App.tsx:11298-11311@8fdbbd6c 78.6%  drop-the-row
//
// ── GROUP ONE: `stopTimerAsk` x `stopTimerForSwap` ──────────────────────────
//
// THREE DIFFERENCES, THREE PARAMETERS (§2), and not one of them a choice:
//
//   WHICH ROW      Ask resolves it from a taskId, falling back to the focused
//                  index; ForSwap is handed the index. -> a `pick` function.
//   THE SEED       ForSwap passes the pending start in the modal seed and Ask
//                  does not. -> a parameter, and passing `undefined` is
//                  PROVED equivalent to omitting it rather than assumed:
//                  `openTimerLogTe` does `pendingKey || null`
//                  (src/App.tsx, the `timerTeRef.current` line), so the two
//                  reach the same value.
//                  BRIEF 116 §3 — that seed was a bare `pendingTaskId` until
//                  events learned to carry timers; it is the {taskId, eventId,
//                  subtaskId} key now, for the reason at the `stop` signature.
//   THE AFTERMATH  ForSwap closes the timer-limit dialog. -> an optional
//                  callback, fired exactly where the statement stood.
//
// ── GROUP TWO: drop the row, clamp the focus ────────────────────────────────
//
// The same filter-and-clamp in `releaseEntityTimer`'s "clear" arm and in the
// time-entry save path.
//
// THE SUBTASK KEY IS PASSED IN, NOT NORMALISED HERE, and that is the whole care
// this one needs. One caller passes `hold.subtaskId` — already normalised
// upstream — and the other passes `_ctm.subtaskId || null`. Normalising inside
// would look tidier and would CHANGE one of them the day `hold.subtaskId` is
// `undefined`: `(t2.subtaskId || null) === undefined` is false where
// `=== null` is true. So each caller keeps passing exactly what it passed, and
// this compares.

export function makeTimerStops(ctx: any) {
  const {
    setActiveTimers,
    setFocusedTimerIdx,
    setTimerLimitDlg,
    openTimerLogTe,
    roundUp5,
    focusedTimerIdx,
  } = ctx;

  /* 116 §3 — `pendingTaskId` became `pendingKey`, a {taskId, eventId,
     subtaskId} triple, because the swap it drives re-issues a START and a bare
     task id cannot name an event (or, already, a subtask). */
  const stop = (pick: any, pendingKey: any, after: (() => void) | null) => {
    setActiveTimers((prev: any) => {
      const idx = pick(prev);
      const tm = prev[idx];
      if (!tm) return prev;
      const now = Date.now();
      const el = tm.paused ? tm.elapsed : tm.elapsed + (now - tm.startedAt);
      const mins = Math.max(5, roundUp5(el));
      // C12 — straight to the canonical time-entry modal (main window).
      // BRIEF 91 §3a — `eventId` rides with the pair. A timer whose parent is an
      // agenda event has no taskId, and without this the log opened caseless.
      openTimerLogTe({
        taskId: tm.taskId,
        subtaskId: tm.subtaskId || null,
        eventId: tm.eventId || null,
        mins,
        pendingKey,
      });
      if (after) after();
      setFocusedTimerIdx((i: any) => Math.max(0, Math.min(i, prev.length - 2)));
      return prev.filter((_: any, i: any) => i !== idx);
    });
  };

  return {
    stopTimerAsk: (taskId: any) =>
      stop(
        (prev: any) =>
          taskId != null
            ? prev.findIndex((t: any) => t.taskId === taskId)
            : focusedTimerIdx,
        undefined,
        null,
      ),
    stopTimerForSwap: (idx: any, pendingKey: any) =>
      stop(() => idx, pendingKey, () => setTimerLimitDlg(null)),
  };
}

// `count` is the length the clamp reads — `activeTimers.length` at both call
// sites, passed rather than closed over because both read it from the render
// scope at the moment they fire.
export function dropTimerRow(
  ctx: any,
  taskId: any,
  subtaskId: any,
  /* BRIEF 116 §3 — THE THIRD FIELD, AND HERE IT IS NOT COSMETIC. This filter
     removed every row matching the PAIR, which was safe only while no two rows
     could share it. An event's own timer keys (null, null) — so with two event
     timers running, dropping one dropped BOTH, silently, on the path that
     consumes a timer into a saved time entry (TimeEntryModal.tsx:190). The
     parameter is optional and defaults to the pre-116 behaviour for any caller
     that has not been given the row's kind. */
  eventId?: number | null,
) {
  const { setActiveTimers, setFocusedTimerIdx, count } = ctx;
  setActiveTimers((prev: any) =>
    prev.filter(
      (t2: any) =>
        !(
          t2.taskId === taskId &&
          (t2.subtaskId || null) === subtaskId &&
          (eventId === undefined || (t2.eventId || null) === (eventId || null))
        ),
    ),
  );
  setFocusedTimerIdx((i: any) => Math.max(0, Math.min(i, count - 2)));
}
